import { CSSProperties, FC, useMemo, useState, } from "react";
// 组件
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { ProImage, ProTooltip, } from '../antd';
import CopyText from '../CopyText';
// 方法
import classNames from "classnames";
import { useLayoutContext } from '../index';
import { renderEnumName } from '../utils/renderUtils';
import { formatTootipTitle, getColumnValue } from "../utils/dataUtils";
import { filterEmptyValue } from "../utils/utils";
// 常量
import { TABLE_COLUMN_EMPTY } from '../constants/constant';
import { SkuInfoListProps } from "./interface";
import './style.less';

/** sku信息列 */
const InterSkuInfoList: FC<SkuInfoListProps> = props => {
  const {
    bskuOptions, skuOptions, style, className, width, data, imageFieldName, rowKey = 'key', skuItemDataKey, size,
    placeholder = TABLE_COLUMN_EMPTY, skuItemMaxLength, imageTootipFieldName, addonBeforeLabel, addonBeforeFieldname,
  } = props;
  const [ showAll, setShowAll ] = useState<boolean>(false);
  const { enumData } = useLayoutContext();
  const skuItemList: any[] = useMemo(() => {
    if (!data) return [];
    const newSkuItemList = skuItemDataKey ? data[skuItemDataKey] : data;
    return Array.isArray(newSkuItemList) ? newSkuItemList : [];
  }, [data, skuItemDataKey,]);
  const wrapperClasses = useMemo(() => {
    return classNames('sku-info-wrap', className);
  }, [className]);
  const wrapperStyle: CSSProperties = useMemo(() => {
    let newStyle: CSSProperties = {};
    if (style) newStyle = { ...style };
    if (width) {
      newStyle.width = `${width}px`;
    }
    return newStyle;
  }, [style, width]);
  if (!data) return <span>{placeholder}</span>;
  const newSkuItemList = (skuItemMaxLength !== undefined && !showAll) ? skuItemList.slice(0, skuItemMaxLength) : skuItemList;

  return (
    <div className={wrapperClasses} style={wrapperStyle}>
      {
        Array.isArray(bskuOptions) && !!bskuOptions.length && (
          <div
            className={classNames('sku-info', {
              'sku-info-large': size === 'large',
              'sku-info-middle': size === 'middle',
              'sku-info-small': size === 'small',
            })}
          >
            {
              bskuOptions?.map(option => {
                const { label, name, hiddenTooltip, isCopy, enumCode, valueMaxWidth, className, style, } = option;
                const valueStyle: CSSProperties | undefined = valueMaxWidth ? { maxWidth: valueMaxWidth, } : undefined;
                const renderValue = getColumnValue(data, name);
                const renderText: any = (isCopy && typeof renderValue === 'string') ? (
                  <CopyText text={renderValue} showTooltips={false} />
                ) : (
                  enumCode ? (
                    <span className="padding-right-12">{renderEnumName(renderValue, enumData?.[enumCode]?.items || [])}</span>
                  ) : (
                    <span className="padding-right-12">{filterEmptyValue(renderValue)}</span>
                  )
                );
                return (
                  <div className={classNames('sku-info-option', className)} key={name} style={style}>
                    {
                      !!label && <div className="sku-info-option-label">{label}:</div>
                    }
                    {
                      hiddenTooltip ? (
                        <div className="sku-info-option-text" style={valueStyle}>{renderText}</div>
                      ) : (
                        <ProTooltip title={renderValue}>
                          <div className="sku-info-option-text" style={valueStyle}>{renderText}</div>
                        </ProTooltip>
                      )
                    }
                  </div>
                );
              })
            }
          </div>
        ) 
      }
      {
        newSkuItemList.map((item, index) => {
          return (
            <div
              className="sku-info-item"
              key={item[rowKey]}
              style={{ marginBottom: (newSkuItemList.length - 1) === index ? 0 : 10}}
            >
              {
                (imageFieldName === null) ? null : (
                  <div
                    className={classNames('sku-info-item-image', {
                      'item-image-large': size === 'large',
                      'item-image-middle': size === 'middle',
                      'item-image-small': size === 'small',
                    })}
                  >
                    {
                      imageTootipFieldName ? (
                        <ProTooltip placement="topLeft" title={formatTootipTitle(getColumnValue(data, imageTootipFieldName))}>
                          <ProImage
                            src={getColumnValue(item, imageFieldName)}
                            height={size === 'large' ? 56 : (size === 'small' ? 36 : 45)}
                          />
                        </ProTooltip>
                      ) : (
                        <ProImage
                          src={getColumnValue(item, imageFieldName)}
                          height={size === 'large' ? 56 : (size === 'small' ? 36 : 45)}
                        />
                      )
                    }
                  </div>
                )
              }
              <div className="sku-info-item-info">
                {
                  skuOptions?.map((group, groupKey) => {
                    const { mergeColumn, config, } = group;
                    return mergeColumn ? (
                      <div className="info-row" key={groupKey}>
                        {
                          !!addonBeforeLabel && (
                            <span style={{paddingRight: 5, whiteSpace: 'nowrap',}}>{addonBeforeLabel}</span>
                          )
                        }
                        {
                          !!addonBeforeFieldname && (
                            <span style={{paddingRight: 5}}>{filterEmptyValue(getColumnValue(item, addonBeforeFieldname))}</span>
                          )
                        }
                        {
                          config.map((option, index) => {
                            const { name, hiddenTooltip, className, style, isCopy, } = option;
                            const renderValue = getColumnValue(item, name);
                            const renderText: any = (isCopy && typeof renderValue === 'string') ? (
                              <CopyText text={renderValue} showTooltips={false} />
                            ) : filterEmptyValue(renderValue);
                            return (
                              <div
                                className={classNames('merge-col', className, {
                                  'merge-col-large': size === 'large',
                                  'merge-col-middle': size === 'middle',
                                  'merge-col-small': size === 'small',
                                  'yb-line-ellipsis': index === config.length - 1,
                                })}
                                key={name}
                                style={style}
                              >
                                {
                                  hiddenTooltip ? (
                                    <span className={classNames({'yb-line-ellipsis': index === config.length - 1,})}>{renderText}</span>
                                  ) : (
                                    <ProTooltip title={typeof renderValue === 'string' ? renderValue : ''}>
                                      <span className={classNames({'yb-line-ellipsis': index === config.length - 1,})}>{renderText}</span>
                                    </ProTooltip>
                                  )
                                }
                              </div>
                            );
                          })
                        }
                      </div>
                    ) : (
                      <div className="info-row" key={groupKey}>
                        {
                          config.map(option => {
                            const { label, name, hiddenTooltip, isCopy, className, style, } = option;
                            const renderValue = getColumnValue(item, name);
                            const renderText: any = (isCopy && typeof renderValue === 'string') ? (
                              <CopyText text={renderValue} showTooltips={false} />
                            ) : <span className="padding-right-12">{filterEmptyValue(renderValue)}</span>;
                            return (
                              <div
                                className={classNames('info-col', className, {
                                  'info-col-large': size === 'large',
                                  'info-col-middle': size === 'middle',
                                  'info-col-small': size === 'small',
                                })}
                                key={name}
                                style={style}
                              >
                                {
                                  !!label && (
                                    <div className="info-col-label">{label}:</div>
                                  )
                                }
                                {
                                  hiddenTooltip ? (
                                    <div className="info-col-text">{renderText}</div>
                                  ) : (
                                    <ProTooltip title={typeof renderValue === 'string' ? renderValue : ''}>
                                      <div className="info-col-text">{renderText}</div>
                                    </ProTooltip>
                                  )
                                }
                              </div>
                            );
                          })
                        }
                      </div>
                    );
                  })
                }
              </div>
            </div>
          );
        })
      }
      {
        skuItemMaxLength !== undefined && skuItemList.length > skuItemMaxLength && (
          <div
            className="sku-list-show"
            onClick={() => {
              setShowAll(prev => !prev);
            }}
          >
            {
              showAll ? (
                <>
                  <span>点击收起</span>
                  <UpOutlined />
                </>
              ) : (
                <>
                  <span>剩余{skuItemList.length - 1}条</span>
                  <DownOutlined />
                </>
              )
            }
          </div>
        )
      }
    </div>
  );
};

InterSkuInfoList.defaultProps = {
  imageFieldName: 'image',
  rowKey: 'key',
  placeholder: TABLE_COLUMN_EMPTY,
  size: 'middle',
};

export default InterSkuInfoList;
